// Global Instructions Rule Applied!
// Shape of GET /capital-calls/fund/:fundId/position (cc__fund_position):
// fund-level drawdown summary + one row per closed commitment with called /
// uncalled / paid / outstanding to date (Issued calls only).
import { z } from 'zod'
import { validateWithZodSchema } from '../../core/utils/utilities.ts'

export const CapitalCallPositionSummarySchema = z
  .object({
    committed: z.number().nullish(),
    called: z.number().nullish(),
    // spec A3.9 addendum part 4: % drawn / uncalled run on the amount called
    // on the lines that count toward the commitment; the flags are the
    // fund's commitment_includes_* terms as they apply (unset = included)
    called_toward_commitment: z.number().nullish(),
    commitment_includes_mgmt_fees: z.boolean().nullish(),
    commitment_includes_expenses: z.boolean().nullish(),
    pct_drawn: z.number().nullish(),
    uncalled: z.number().nullish(),
    paid: z.number().nullish(),
    outstanding: z.number().nullish(),
    called_by_type: z.record(z.string(), z.number()).nullish()
  })
  .passthrough()

export const CapitalCallPositionRowSchema = z
  .object({
    fund_commitments__id: z.string(),
    investors__id: z.string().nullish(),
    investors__name: z.string().nullish(),
    fund_commitments__amount: z.number().nullish(),
    prog__called_to_date: z.number().nullish(),
    prog__called_toward_commitment: z.number().nullish(),
    prog__called_by_type: z.record(z.string(), z.number()).nullish(),
    prog__pct_drawn: z.number().nullish(),
    prog__uncalled: z.number().nullish(),
    prog__paid_amount: z.number().nullish(),
    prog__outstanding: z.number().nullish()
  })
  .passthrough()

export const CapitalCallPositionSchema = z
  .object({
    summary: CapitalCallPositionSummarySchema,
    rows: z.array(CapitalCallPositionRowSchema),
    called_types: z.array(z.string()).optional().default([])
  })
  .passthrough()

export type CapitalCallPositionSummary = z.infer<typeof CapitalCallPositionSummarySchema>
export type CapitalCallPositionRow = z.infer<typeof CapitalCallPositionRowSchema>
export type CapitalCallPosition = z.infer<typeof CapitalCallPositionSchema>

export const validateCapitalCallPosition = (data: unknown): CapitalCallPosition =>
  validateWithZodSchema(CapitalCallPositionSchema, data, 'capital call position')
